import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import * as Yup from 'yup';
import { useFormik } from 'formik';

import { _Object } from 'utils/interfaces';
import { capitalizeFirstLetter, closeModal, doAmountFormat, handleCartTotal, routes, strings } from 'utils';
import { currenciesArray } from 'utils/currencies';
import { incrementQuantity, decrementQuantity, removeCartItem, addToCart, setCustomer } from 'redux/slices/cart.slice';

import { TextArea } from 'views/components';
import { Button, SearchField } from '../../../components/form-inputs';
import CustomerModal from './modal';
import { userService } from 'services';
import TodaySalesSummary from './summary';
import CartDiscountModal from './cart-discount-modal';
import { useLocation } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faXmark,
  faPrint,
  faFileLines,
  faPercent,
  faCheck,
  faArrowRight,
  faTrash,
  faCartShopping,
  faUser,
  faPlus
} from '@fortawesome/free-solid-svg-icons';
import HeaderMobile from 'views/components/header-mobile';

const MobileTemplate = ({ orderFormik, btnLoading }: _Object) => {

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const { cart } = useSelector((state: _Object) => state.cart);
  const { me } = useSelector((state: _Object) => state.session);

  const [cartTotal, setCartTotal] = useState<_Object>({});

  // Customer search step toggle matching desktop template1
  const [isCustomerSearchOpen, setIsCustomerSearchOpen] = useState(false);
  const [customerSearchQuery, setCustomerSearchQuery] = useState('');
  const [customerSearchResults, setCustomerSearchResults] = useState<any[]>([]);
  const [isCustomerSearching, setIsCustomerSearching] = useState(false);
  const [selectedCustomerIndex, setSelectedCustomerIndex] = useState<number | null>(null);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isCustomerSearchOpen) {
        setIsCustomerSearchOpen(false);
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isCustomerSearchOpen]);

  const handleCustomerSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setCustomerSearchQuery(query);
    setSelectedCustomerIndex(null);
    if (!query.trim()) {
      setCustomerSearchResults([]);
      setIsCustomerSearching(false);
      return;
    }
    setIsCustomerSearching(true);
    userService
      .getCustomerSearch({ q: query.trim() })
      .then((res: any) => {
        setCustomerSearchResults(res?.items || []);
        setIsCustomerSearching(false);
      })
      .catch(() => {
        setIsCustomerSearching(false);
      });
  };

  const handleCustomerKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (customerSearchResults.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedCustomerIndex((prev) =>
          prev === null || prev === customerSearchResults.length - 1 ? 0 : prev + 1
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedCustomerIndex((prev) =>
          prev === null || prev === 0 ? customerSearchResults.length - 1 : prev - 1
        );
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const indexToSelect = selectedCustomerIndex !== null ? selectedCustomerIndex : 0;
        if (customerSearchResults[indexToSelect]) {
          handleSelectCustomer(customerSearchResults[indexToSelect]);
        }
      } else if (e.key === 'Escape') {
        setIsCustomerSearchOpen(false);
      }
    }
  };

  const handleSelectCustomer = (customer: any) => {
    dispatch(setCustomer(customer));
    setCustomerSearchQuery('');
    setSelectedCustomerIndex(null);
    setIsCustomerSearchOpen(false);
  };

  // Payment panels state matching desktop template1
  const [activePaymentMode, setActivePaymentMode] = useState<string>('cash');
  const [showNotePanel, setShowNotePanel] = useState<boolean>(false);
  const [discountValue, setDiscountValue] = useState<number | string>(0);
  const [activeDiscountPercent, setActiveDiscountPercent] = useState<number | null>(null);
  const [cashReceived, setCashReceived] = useState<number | string>('');
  const [cardType, setCardType] = useState<string>('Debit Card');
  const [cardRemark, setCardRemark] = useState<string>('');
  const [upiRemark, setUpiRemark] = useState<string>('');
  const [splitPayments] = useState<Array<{ id: string; method: string; amount: number | string }>>([
    { id: '1', method: 'Cash', amount: '' },
    { id: '2', method: 'UPI', amount: '' },
    { id: '3', method: 'Card', amount: '' }
  ]);
  const notePopoverRef = useRef<HTMLDivElement>(null);

  const discountFormik = useFormik({
    initialValues: { label: '', amount: '' },
    validationSchema: Yup.object({
      amount: Yup.number()
        .label('Discount amount')
        .positive('Amount must be greater than zero')
        .required()

    }),
    enableReinitialize: true,
    onSubmit: () => {
      closeModal('discount-modal');
      discountFormik.resetForm();
    },
  });

  useEffect(() => {
    const response = handleCartTotal(cart, me);
    setCartTotal(response);
  }, [cart, me]);

  // Calculations for subtotal, tax, discount, total
  const subTotal = Number(cartTotal?.subTotal) || 0;
  const taxAmount = Number(cartTotal?.tax) || 0;
  const discountNum = Number(discountValue) || 0;
  const finalTotal = Math.max(0, subTotal + taxAmount - discountNum);
  const totalItemCount = cart?.lineItems?.reduce((acc: number, item: any) => acc + (Number(item?.quantity) || 1), 0) || 0;

  const currencySymbol = currenciesArray.find((item) => item.label === me?.client?.currency)?.value || process.env.REACT_APP_CURRENCY || '₹';
  const taxRatePercentage = subTotal > 0 && taxAmount > 0 
    ? Math.round((taxAmount / (subTotal - discountNum > 0 ? subTotal - discountNum : subTotal)) * 100) 
    : 0;

  const dynamicDenominations = (() => {
    if (finalTotal <= 0) return [200, 500, 1000];
    if (finalTotal <= 100) return [100, 200, 500];
    if (finalTotal <= 200) return [200, 500, 1000];
    if (finalTotal <= 500) return [500, 1000, 2000];
    if (finalTotal <= 1000) return [1000, 1500, 2000];
    const base = Math.ceil(finalTotal / 500) * 500;
    const next500 = base === finalTotal ? base + 500 : base;
    const next1000 = Math.ceil(finalTotal / 1000) * 1000;
    const higher = next1000 <= next500 ? next500 + 500 : next1000;
    return Array.from(new Set([next500, higher, higher + 500])).slice(0, 3);
  })();

  useEffect(() => {
    orderFormik.setFieldValue('amount', finalTotal);
    orderFormik.setFieldValue('discount', discountNum);
    orderFormik.setFieldValue('cash_received', cashReceived);
    if (activePaymentMode === 'card') {
      orderFormik.setFieldValue('card_type', cardType);
      orderFormik.setFieldValue('remark', cardRemark);
      orderFormik.setFieldValue('transaction_data', { card_type: cardType, remark: cardRemark });
    } else if (activePaymentMode === 'upi') {
      orderFormik.setFieldValue('remark', upiRemark);
      orderFormik.setFieldValue('transaction_data', { remark: upiRemark });
    } else if (activePaymentMode === 'split') {
      orderFormik.setFieldValue('split_payments', splitPayments);
      orderFormik.setFieldValue('transaction_data', { split_payments: splitPayments });
    } else {
      orderFormik.setFieldValue('transaction_data', { cash_received: cashReceived });
    }
  }, [finalTotal, discountNum, cashReceived, cardType, cardRemark, upiRemark, splitPayments, activePaymentMode]);

  useEffect(() => {
    if (!orderFormik.values.payment_mode) {
      orderFormik.setFieldValue('payment_mode', 'cash');
    }
  }, []);

  useEffect(() => {
    if (finalTotal > 0 && (cashReceived === '' || cashReceived === 0)) {
      setCashReceived(finalTotal);
    }
  }, [finalTotal]);

  const receivedNum = Number(cashReceived) || 0;
  const changeToReturn = Math.max(0, receivedNum - finalTotal);

  const handleSelectMode = (mode: string) => {
    setActivePaymentMode(mode);
    orderFormik.setFieldValue('payment_mode', mode);
    setShowNotePanel(false);
  };

  // Close note popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (notePopoverRef.current && !notePopoverRef.current.contains(event.target as Node)) {
        setShowNotePanel(false);
      }
    };
    if (showNotePanel) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showNotePanel]);

  const isCustomerSelected = Boolean(
    (cart?.customer?.id && cart?.customer?.id !== '') ||
    (cart?.customer?._id && cart?.customer?._id !== '')
  );
  const isPlaceOrderDisabled = Boolean(btnLoading || !cart?.lineItems?.length || !isCustomerSelected);
  const placeOrderButtonTitle = !isCustomerSelected
    ? 'Please select a customer first'
    : !cart?.lineItems?.length
      ? 'Cart is empty'
      : undefined;

  const [modal, setModal] = useState(false);

  return (
    <>
      <div className="d-block d-md-none position-sticky top-0 z-3 pt-2">
        <HeaderMobile onSummaryOpen={() => setModal(true)} />
      </div>


      <div
        className="offcanvas offcanvas-end"
        tabIndex={-1}
        id="userSearch"
        aria-labelledby="userSearchLabel"
        style={{
          width: '100%',
          maxWidth: '460px'
        }}
      >
        <div className="offcanvas-header border-bottom py-2 px-3 d-flex align-items-center justify-content-between bg-light flex-shrink-0">
          <h6 className="offcanvas-title fw-bold m-0 text-dark small text-uppercase" style={{ letterSpacing: '0.5px' }}>Checkout</h6>
          <button type="button" className="btn-close" data-bs-dismiss="offcanvas" aria-label="Close"></button>
        </div>

        <div className="offcanvas-body d-flex flex-column p-0 overflow-hidden flex-grow-1">
          {/* Top Customer Bar (Desktop Matched) */}
          <div className="p-3 pb-2 flex-shrink-0 bg-white border-bottom border-light-subtle">
            <div className="pos-customer-top-bar d-flex align-items-center gap-2 w-100 flex-shrink-0">
              <div className="pos-customer-slot flex-grow-1 min-w-0 d-flex align-items-center">
                {isCustomerSelected && !isCustomerSearchOpen ? (
                  /* Customer selected: Name and mobile number design matching Desktop Image 2 */
                  <div
                    className="pos-customer-card d-flex align-items-center justify-content-between gap-2 p-2 rounded cursor-pointer w-100"
                    onClick={() => setIsCustomerSearchOpen(true)}
                    title="Click to search / change customer"
                  >
                    <div className="pos-customer-card-left d-flex align-items-center gap-2 min-w-0 flex-grow-1 overflow-hidden">
                      <div className="pos-customer-avatar rounded-circle d-flex align-items-center justify-content-center flex-shrink-0">
                        <FontAwesomeIcon icon={faUser} />
                      </div>
                      <div className="pos-customer-info d-flex flex-column justify-content-center min-w-0 flex-grow-1 overflow-hidden">
                        <span className="pos-customer-name text-truncate fw-bold text-dark small">
                          {cart.customer.full_name ||
                            cart.customer.name ||
                            `${cart.customer.first_name || ''} ${cart.customer.last_name || ''}`.trim() ||
                            'Customer'}
                        </span>
                        <span className="pos-customer-details text-truncate text-muted" style={{ fontSize: '11px' }}>
                          {cart.customer.mobile_number || cart.customer.phone || 'Customer details'}
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="pos-customer-clear-btn btn btn-link text-muted p-1 border-0 d-flex align-items-center justify-content-center flex-shrink-0"
                      title="Clear customer"
                      onClick={(e) => {
                        e.stopPropagation();
                        dispatch(setCustomer({}));
                        setIsCustomerSearchOpen(false);
                      }}
                    >
                      <FontAwesomeIcon icon={faXmark} />
                    </button>
                  </div>
                ) : (
                  /* Customer Search Input using standard SearchField (Desktop Image 1) */
                  <SearchField
                    name="q"
                    id="customerSearchFieldMobile"
                    type="search"
                    autoComplete="off"
                    className="mb-0 w-100"
                    placeholder={t('customer.search_customer') || 'Search customer...'}
                    value={customerSearchQuery}
                    autoFocus={isCustomerSearchOpen}
                    onClick={() => {
                      if (!isCustomerSearchOpen) setIsCustomerSearchOpen(true);
                    }}
                    onFocus={() => {
                      if (!isCustomerSearchOpen) setIsCustomerSearchOpen(true);
                    }}
                    onChange={handleCustomerSearchChange}
                    onKeyDown={handleCustomerKeyDown}
                  />
                )}
              </div>

              {/* Add Customer Button (+) matching Desktop */}
              <Button
                type="button"
                className="light border border-light-subtle rounded flex-shrink-0 d-flex align-items-center justify-content-center pos-customer-add-btn"
                style={{ width: '40px', minWidth: '40px', height: '40px', padding: 0 }}
                data-bs-toggle="modal"
                data-bs-target="#customer-modal-sales"
              >
                <FontAwesomeIcon icon={faPlus} className="me-0" />
              </Button>
            </div>

            {/* {!isCustomerSelected && !isCustomerSearchOpen && (
              <div className="alert alert-warning py-1 px-2 mt-2 mb-0 small text-center rounded">
                Please select or add a customer to place order
              </div>
            )} */}
          </div>

          {location.pathname === `${routes.sales}` && (
            <div className="d-flex flex-column flex-grow-1 overflow-hidden">
              {isCustomerSearchOpen ? (
                /* Customer Search View: List of search results matching Desktop Image 1 */
                <div className="customer-list flex-grow-1 overflow-y-auto px-3 py-2 bg-white">
                  <div className="d-flex align-items-center justify-content-between pb-2 mb-2 border-bottom border-light-subtle">
                    <span className="fw-bold small text-muted text-uppercase" style={{ letterSpacing: '0.5px' }}>
                      {customerSearchResults.length > 0 ? `Customers (${customerSearchResults.length})` : 'Search Customer'}
                    </span>
                    <button
                      type="button"
                      className="btn btn-link text-secondary text-decoration-none p-0 small fw-semibold"
                      onClick={() => {
                        setIsCustomerSearchOpen(false);
                        setCustomerSearchQuery('');
                      }}
                    >
                      Cancel
                    </button>
                  </div>

                  {isCustomerSearching ? (
                    <div className="text-center py-4 text-muted">
                      <div className="spinner-border spinner-border-sm text-primary mb-2" role="status"></div>
                      <p className="mb-0" style={{ fontSize: '13px' }}>Searching customers...</p>
                    </div>
                  ) : customerSearchResults.length > 0 ? (
                    <ul className="list-group">
                      {customerSearchResults.map((data: any, i: number) => {
                        const makeAvatar = (firstName: string, lastName: string) => {
                          const capitalize = (name: string) =>
                            name?.charAt(0)?.toUpperCase() + name?.slice(1)?.toLowerCase();
                          const firstInitial = capitalize(firstName || '')[0] || '';
                          const lastInitial = capitalize(lastName || '')[0] || '';
                          return `${firstInitial}${lastInitial}`;
                        };

                        const avatar = makeAvatar(data?.first_name, data?.last_name);

                        return (
                          <li
                            key={data.id || data._id || i}
                            className={`list-group-item ${selectedCustomerIndex === i ? 'active' : ''}`}
                            aria-current="true"
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleSelectCustomer(data)}
                          >
                            <div className="d-flex align-items-center">
                              <div className="rounded-circle bg-light my-auto avtar d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: '36px', height: '36px' }}>
                                <span className="mb-0 fw-bold small text-primary">{avatar}</span>
                              </div>
                              <aside className="ms-2 min-w-0 flex-grow-1">
                                <span className="fw-semibold text-dark d-block text-truncate">{data?.full_name || data.mobile_number}</span>
                                <span className="m-0 d-block text-muted small">{data.mobile_number}</span>
                              </aside>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  ) : customerSearchQuery.trim() ? (
                    <div className="text-center py-4">
                      <p className="text-danger mb-0" style={{ fontSize: '13px' }}>{strings.noRecordFound}</p>
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm mt-3"
                        data-bs-toggle="modal"
                        data-bs-target="#customer-modal-sales"
                      >
                        <FontAwesomeIcon icon={faPlus} className="me-1" /> Add New Customer
                      </button>
                    </div>
                  ) : (
                    <div className="text-center py-4 text-muted small">
                      Type a customer name or mobile number to search
                    </div>
                  )}
                </div>
              ) : cart?.lineItems?.length > 0 ? (
                /* Cart Items + Payment Pinned Section (Normal Checkout) */
                <div className="d-flex flex-column flex-grow-1 overflow-hidden">
                  <div className="pos-cart-items-wrapper flex-grow-1 overflow-y-auto px-3 py-2">
                    <div className="d-flex align-items-center justify-content-between pb-2 border-bottom border-light-subtle">
                      <span className="fw-bold small text-dark">Cart Items ({totalItemCount})</span>
                      <button
                        type="button"
                        className="btn btn-link text-danger text-decoration-none p-0 small fw-semibold d-inline-flex align-items-center gap-1"
                        onClick={() => dispatch(addToCart([]))}
                      >
                        <FontAwesomeIcon icon={faTrash} /> Clear
                      </button>
                    </div>

                    <div className="pos-cart-items-list d-flex flex-column gap-2 pt-2">
                      {cart.lineItems.map((item: any, index: number) => (
                        <div key={item.id || index} className="pos-cart-item-row d-flex align-items-start gap-2 py-1 border-bottom border-light-subtle">
                          <div className="pos-item-thumbnail rounded overflow-hidden flex-shrink-0 d-flex align-items-center justify-content-center bg-light" style={{ width: '44px', height: '44px' }}>
                            {item.image ? (
                              <img src={item.image} alt={item.name} className="w-100 h-100" style={{ objectFit: 'cover' }} />
                            ) : (
                              <div className="fw-bold text-muted small">{capitalizeFirstLetter(item.name || '').slice(0, 2).toUpperCase()}</div>
                            )}
                          </div>

                          <div className="flex-grow-1 min-w-0">
                            <div className="pos-item-name fw-semibold text-dark small text-truncate" title={item.name}>
                              {capitalizeFirstLetter(item.name)}
                            </div>
                            <div className="text-muted small" style={{ fontSize: '11px' }}>
                              {doAmountFormat(item.price)}
                            </div>

                            <div className="d-flex align-items-center justify-content-between mt-1">
                              <div className="pos-item-qty-stepper d-inline-flex align-items-center border border-light-subtle bg-white rounded overflow-hidden">
                                <button
                                  type="button"
                                  className="border-0 bg-transparent text-primary fw-bold px-2"
                                  onClick={() => dispatch(decrementQuantity(item.id))}
                                >
                                  -
                                </button>
                                <span className="fw-bold text-dark px-1 small">{item.quantity}</span>
                                <button
                                  type="button"
                                  className="border-0 bg-transparent text-primary fw-bold px-2"
                                  onClick={() => dispatch(incrementQuantity(item.id))}
                                >
                                  +
                                </button>
                              </div>

                              <div className="d-flex align-items-center gap-2">
                                <span className="fw-bold text-dark small">{doAmountFormat(item.price * item.quantity)}</span>
                                <button
                                  type="button"
                                  className="btn btn-link text-danger p-0 border-0"
                                  onClick={() => dispatch(removeCartItem(index))}
                                >
                                  <FontAwesomeIcon icon={faTrash} />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div
                    className="pos-checkout-sidebar-content flex-shrink-0 bg-white border-top shadow-sm px-3 pt-2 pb-3 overflow-y-auto"
                    style={{ maxHeight: '60vh' }}
                  >
                    <form onSubmit={orderFormik.handleSubmit}>
                      <div className="pos-quick-actions-bar position-relative d-flex align-items-center gap-2 py-2 my-0 my-md-2 border-bottom border-light-subtle">
                        <div className="pos-note-wrapper position-relative" ref={notePopoverRef}>
                          <button
                            type="button"
                            className={`pos-action-chip pos-action-note btn btn-sm d-inline-flex align-items-center gap-1 rounded fw-semibold ${showNotePanel || orderFormik.values.order_note ? 'active' : ''}`}
                            onClick={() => setShowNotePanel(!showNotePanel)}
                          >
                            <FontAwesomeIcon icon={faFileLines} className="pos-chip-icon" /> Note
                            {Boolean(orderFormik.values.order_note) && (
                              <span className="badge bg-primary rounded-circle p-1 ms-1" style={{ width: '6px', height: '6px', display: 'inline-block' }} />
                            )}
                          </button>

                          {showNotePanel && (
                            <div className="pos-note-popover position-absolute bg-white border border-light-subtle rounded shadow">
                              <div className="pos-note-popover-header d-flex align-items-center justify-content-between mb-2">
                                <span className="pos-note-title fw-bold small text-dark d-flex align-items-center gap-1">
                                  <FontAwesomeIcon icon={faFileLines} className="text-primary small" /> Add note to this order
                                </span>
                                <button
                                  type="button"
                                  className="pos-note-close-btn btn btn-link text-muted p-0 border-0"
                                  onClick={() => setShowNotePanel(false)}
                                  title="Close note"
                                >
                                  <FontAwesomeIcon icon={faXmark} />
                                </button>
                              </div>

                              <TextArea
                                name="order_note"
                                placeholder="Enter here"
                                rows={2}
                                className="mb-1"
                                value={orderFormik.values.order_note || ''}
                                onChange={(e: any) => orderFormik.setFieldValue('order_note', e.target.value.slice(0, 300))}
                              />

                              <div className="pos-note-footer d-flex align-items-center justify-content-end gap-2">
                                <button
                                  type="button"
                                  className="pos-btn-cancel btn btn-sm d-inline-flex align-items-center justify-content-center"
                                  onClick={() => setShowNotePanel(false)}
                                  title="Cancel"
                                  aria-label="Cancel"
                                >
                                  <FontAwesomeIcon icon={faXmark} />
                                </button>
                                <button
                                  type="button"
                                  className="pos-btn-save btn btn-sm d-inline-flex align-items-center justify-content-center"
                                  onClick={() => setShowNotePanel(false)}
                                  title="Save Note"
                                  aria-label="Save Note"
                                >
                                  <FontAwesomeIcon icon={faCheck} />
                                </button>
                              </div>
                            </div>
                          )}
                        </div>

                        <button
                          type="button"
                          className="pos-action-chip pos-action-discount btn btn-sm d-inline-flex align-items-center gap-1 rounded fw-semibold"
                          onClick={() => {
                            const input = document.getElementById('posDiscountInputMobile');
                            input?.focus();
                          }}
                        >
                          <FontAwesomeIcon icon={faPercent} className="pos-chip-icon" /> Discount
                        </button>
                      </div>

                      <div className="pos-summary-section d-flex flex-column gap-1">
                        <div className="pos-summary-row d-flex align-items-center justify-content-between">
                          <span className="pos-summary-label text-secondary small fw-medium">Items ({totalItemCount})</span>
                          <span className="pos-summary-value text-dark fw-bold small">{doAmountFormat(subTotal)}</span>
                        </div>

                        <div className="pos-summary-row d-flex align-items-center justify-content-between">
                          <span className="pos-summary-label text-secondary small fw-medium">Discount</span>
                          <div className="d-flex align-items-center gap-2">
                            <div className="pos-discount-box d-inline-flex align-items-center border border-light-subtle rounded bg-white px-2">
                              <span className="pos-currency-symbol text-muted small me-1">{currencySymbol}</span>
                              <input
                                id="posDiscountInputMobile"
                                type="number"
                                min="0"
                                value={discountValue}
                                onChange={(e) => {
                                  setActiveDiscountPercent(null);
                                  setDiscountValue(e.target.value === '' ? '' : Number(e.target.value));
                                }}
                                onFocus={(e) => e.target.select()}
                                className="pos-discount-input-field border-0 bg-transparent fw-bold text-dark w-100"
                              />
                            </div>
                            <button
                              type="button"
                              className={`pos-discount-percent-btn pos-percent-btn btn btn-sm rounded fw-semibold ${activeDiscountPercent === 5 ? 'active' : ''}`}
                              onClick={() => {
                                if (activeDiscountPercent === 5) {
                                  setActiveDiscountPercent(null);
                                  setDiscountValue(0);
                                } else {
                                  setActiveDiscountPercent(5);
                                  setDiscountValue(Number((subTotal * 0.05).toFixed(2)));
                                }
                              }}
                            >
                              5%
                            </button>
                            <button
                              type="button"
                              className={`pos-discount-percent-btn pos-percent-btn btn btn-sm rounded fw-semibold ${activeDiscountPercent === 10 ? 'active' : ''}`}
                              onClick={() => {
                                if (activeDiscountPercent === 10) {
                                  setActiveDiscountPercent(null);
                                  setDiscountValue(0);
                                } else {
                                  setActiveDiscountPercent(10);
                                  setDiscountValue(Number((subTotal * 0.10).toFixed(2)));
                                }
                              }}
                            >
                              10%
                            </button>
                          </div>
                        </div>

                        <div className="pos-summary-row d-flex align-items-center justify-content-between">
                          <span className="pos-summary-label text-secondary small fw-medium">Tax ({taxRatePercentage}%)</span>
                          <span className="pos-summary-value text-dark fw-bold small">{doAmountFormat(taxAmount)}</span>
                        </div>

                        <div className="pos-summary-row pos-total-row d-flex align-items-center justify-content-between pt-1 mt-1">
                          <span className="pos-total-label fs-5 fw-bolder text-dark">Total</span>
                          <span className="pos-total-value fs-4 fw-bolder text-dark">{doAmountFormat(finalTotal)}</span>
                        </div>
                      </div>

                      <div className="pos-payment-modes-grid d-flex gap-2 w-100 mt-2">
                        {(['cash', 'upi', 'card'] as const).map((mode) => {
                          const labels: Record<string, string> = {
                            cash: 'Cash',
                            upi: 'UPI',
                            card: 'Card',
                            split: 'Split'
                          };
                          const isActive = activePaymentMode === mode && !showNotePanel;
                          return (
                            <button
                              key={mode}
                              type="button"
                              className={`pos-payment-mode-tab flex-grow-1 border-0 rounded fw-semibold small ${isActive ? 'active' : ''}`}
                              onClick={() => handleSelectMode(mode)}
                            >
                              {labels[mode]}
                            </button>
                          );
                        })}
                      </div>

                      {activePaymentMode === 'cash' ? (
                        <div className="pos-payment-panel mt-2 p-3 bg-white border border-light-subtle rounded">
                          <div className="mb-2">
                            <label className="pos-field-label fw-semibold small text-secondary mb-1 d-block">Amount received</label>
                            <div className="pos-cash-tender-row d-flex align-items-center gap-2 flex-wrap mb-2">
                              <div className="pos-cash-input-box d-inline-flex align-items-center border border-light-subtle rounded bg-white px-2">
                                <span className="pos-currency-symbol text-muted small me-1">{currencySymbol}</span>
                                <input
                                  type="number"
                                  min="0"
                                  value={cashReceived}
                                  onChange={(e) => setCashReceived(e.target.value)}
                                  className="pos-cash-input-field border-0 bg-transparent fw-bold text-dark w-100"
                                />
                              </div>

                              <div className="pos-chips-group d-flex align-items-center gap-2 flex-wrap">
                                <button
                                  type="button"
                                  className="pos-denom-pill btn btn-light btn-sm fw-semibold text-primary rounded px-2"
                                  onClick={() => setCashReceived(finalTotal)}
                                >
                                  Exact
                                </button>
                                {dynamicDenominations.map((denom) => (
                                  <button
                                    key={denom}
                                    type="button"
                                    className="pos-denom-pill btn btn-light btn-sm fw-semibold text-primary rounded px-2"
                                    onClick={() => setCashReceived(denom)}
                                  >
                                    {denom}
                                  </button>
                                ))}
                              </div>
                            </div>
                          </div>

                          <div className="pos-change-row d-flex align-items-center justify-content-between pt-2 mt-2 border-top border-light-subtle">
                            <span className="pos-change-label text-secondary small fw-medium">Change to return</span>
                            <span className="pos-change-amount text-success fw-bold fs-6">{doAmountFormat(changeToReturn)}</span>
                          </div>

                          <div className="pos-checkout-btn-wrap mt-3">
                            <button
                              type="submit"
                              className="pos-complete-payment-btn btn btn-primary w-100 d-flex align-items-center justify-content-between fw-bold py-2 px-3 rounded"
                              disabled={isPlaceOrderDisabled}
                              title={placeOrderButtonTitle}
                              onClick={() => {
                                orderFormik.setFieldValue('payment_mode', 'cash');
                                if (cart?.discount) orderFormik.setFieldValue('discount', cart.discount);
                              }}
                            >
                              <span></span>
                              <span className="pos-pay-btn-label">Place Order</span>
                              <span className="pos-pay-btn-hotkey">
                                <FontAwesomeIcon icon={faPrint} className="me-1" /> P1
                              </span>
                            </button>
                          </div>
                        </div>
                      ) : activePaymentMode === 'upi' ? (
                        <div className="pos-payment-panel mt-2 p-3 bg-white border border-light-subtle rounded">
                          <div className="row g-2 mb-2">
                            <div className="col-6">
                              <div className="pos-field-group mb-0">
                                <label className="pos-field-label fw-semibold small text-secondary mb-1 d-block">Amount received</label>
                                <div className="pos-amount-received-box d-flex align-items-center border border-light-subtle rounded bg-white px-2 w-100">
                                  <span className="pos-currency-symbol text-muted small me-1">{currencySymbol}</span>
                                  <input
                                    type="number"
                                    min="0"
                                    value={cashReceived}
                                    onChange={(e) => setCashReceived(e.target.value)}
                                    className="pos-cash-input-field border-0 bg-transparent fw-bold text-dark w-100"
                                  />
                                </div>
                              </div>
                            </div>

                            <div className="col-6">
                              <div className="pos-field-group mb-0">
                                <label className="pos-field-label fw-semibold small text-secondary mb-1 d-block">
                                  Reference no. <span className="pos-req-star text-danger">*</span>
                                </label>
                                <input
                                  type="text"
                                  className="pos-field-input pos-amount-ref-input form-control form-control-sm"
                                  placeholder="Enter here"
                                  name="payment_ref_number"
                                  value={orderFormik.values.payment_ref_number}
                                  onChange={orderFormik.handleChange}
                                />
                              </div>
                            </div>
                          </div>

                          <div className="pos-field-group mb-2">
                            <label className="pos-field-label fw-semibold small text-secondary mb-1 d-block">Remark</label>
                            <input
                              type="text"
                              className="pos-field-input form-control form-control-sm"
                              placeholder="Enter remark"
                              value={upiRemark}
                              onChange={(e) => setUpiRemark(e.target.value)}
                            />
                          </div>

                          <div className="pos-checkout-btn-wrap mt-3">
                            <button
                              type="submit"
                              className="pos-complete-payment-btn btn btn-primary w-100 d-flex align-items-center justify-content-between fw-bold py-2 px-3 rounded"
                              disabled={isPlaceOrderDisabled}
                              title={placeOrderButtonTitle}
                              onClick={() => {
                                orderFormik.setFieldValue('payment_mode', 'upi');
                                orderFormik.setFieldValue('remark', upiRemark);
                                if (cart?.discount) orderFormik.setFieldValue('discount', cart.discount);
                              }}
                            >
                              <span></span>
                              <span className="pos-pay-btn-label">Place Order</span>
                              <span className="pos-pay-btn-hotkey">
                                <FontAwesomeIcon icon={faPrint} className="me-1" /> P1
                              </span>
                            </button>
                          </div>
                        </div>
                      ) : activePaymentMode === 'card' ? (
                        <div className="pos-payment-panel mt-2 p-3 bg-white border border-light-subtle rounded">
                          <div className="row g-2 mb-2">
                            <div className="col-6">
                              <div className="pos-field-group mb-0">
                                <label className="pos-field-label fw-semibold small text-secondary mb-1 d-block">Amount received</label>
                                <div className="pos-amount-received-box d-flex align-items-center border border-light-subtle rounded bg-white px-2 w-100">
                                  <span className="pos-currency-symbol text-muted small me-1">{currencySymbol}</span>
                                  <input
                                    type="number"
                                    min="0"
                                    value={cashReceived}
                                    onChange={(e) => setCashReceived(e.target.value)}
                                    className="pos-cash-input-field border-0 bg-transparent fw-bold text-dark w-100"
                                  />
                                </div>
                              </div>
                            </div>

                            <div className="col-6">
                              <div className="pos-field-group mb-0">
                                <label className="pos-field-label fw-semibold small text-secondary mb-1 d-block">
                                  Reference no. <span className="pos-req-star text-danger">*</span>
                                </label>
                                <input
                                  type="text"
                                  className="pos-field-input pos-amount-ref-input form-control form-control-sm"
                                  placeholder="Enter here"
                                  name="payment_ref_number"
                                  value={orderFormik.values.payment_ref_number}
                                  onChange={orderFormik.handleChange}
                                />
                              </div>
                            </div>
                          </div>

                          <div className="pos-field-group mb-2">
                            <label className="pos-field-label fw-semibold small text-secondary mb-1 d-block">Card type</label>
                            <div className="d-flex align-items-center gap-3 pt-1">
                              <div className="form-check mb-0">
                                <input
                                  className="form-check-input cursor-pointer"
                                  type="radio"
                                  name="card_type_mobile"
                                  id="card-type-debit-mobile"
                                  value="Debit Card"
                                  checked={cardType === 'Debit Card'}
                                  onChange={(e) => setCardType(e.target.value)}
                                />
                                <label className="form-check-label small cursor-pointer" htmlFor="card-type-debit-mobile">
                                  Debit Card
                                </label>
                              </div>
                              <div className="form-check mb-0">
                                <input
                                  className="form-check-input cursor-pointer"
                                  type="radio"
                                  name="card_type_mobile"
                                  id="card-type-credit-mobile"
                                  value="Credit Card"
                                  checked={cardType === 'Credit Card'}
                                  onChange={(e) => setCardType(e.target.value)}
                                />
                                <label className="form-check-label small cursor-pointer" htmlFor="card-type-credit-mobile">
                                  Credit Card
                                </label>
                              </div>
                            </div>
                          </div>

                          <div className="pos-field-group mb-2">
                            <label className="pos-field-label fw-semibold small text-secondary mb-1 d-block">Remark</label>
                            <input
                              type="text"
                              className="pos-field-input form-control form-control-sm"
                              placeholder="Enter remark"
                              value={cardRemark}
                              onChange={(e) => setCardRemark(e.target.value)}
                            />
                          </div>

                          <div className="pos-checkout-btn-wrap mt-3">
                            <button
                              type="submit"
                              className="pos-complete-payment-btn btn btn-primary w-100 d-flex align-items-center justify-content-between fw-bold py-2 px-3 rounded"
                              disabled={isPlaceOrderDisabled}
                              title={placeOrderButtonTitle}
                              onClick={() => {
                                orderFormik.setFieldValue('payment_mode', 'card');
                                orderFormik.setFieldValue('card_type', cardType);
                                orderFormik.setFieldValue('remark', cardRemark);
                                if (cart?.discount) orderFormik.setFieldValue('discount', cart.discount);
                              }}
                            >
                              <span></span>
                              <span className="pos-pay-btn-label">Place Order</span>
                              <span className="pos-pay-btn-hotkey">
                                <FontAwesomeIcon icon={faPrint} className="me-1" /> P1
                              </span>
                            </button>
                          </div>
                        </div>
                      ) : null}

                      {/* 4. Split Payment Panel (commented out)
                      activePaymentMode === 'split' ? (
                        <div className="pos-payment-panel mt-2 p-3 bg-white border border-light-subtle rounded">
                          <div className="pos-split-list d-flex flex-column gap-2 mb-2">
                            {splitPayments.map((item, idx) => (
                              <div key={item.id} className="pos-split-row d-flex align-items-center gap-2">
                                <span className="pos-split-method d-flex align-items-center gap-1 fw-bold small text-dark" style={{ width: '70px' }}>
                                  {item.method === 'Cash' && <FontAwesomeIcon icon={faMoneyBillWave} />}
                                  {item.method === 'UPI' && <FontAwesomeIcon icon={faQrcode} />}
                                  {item.method === 'Card' && <FontAwesomeIcon icon={faCreditCard} />}
                                  {item.method}
                                </span>
                                <div className="pos-split-input-wrap flex-grow-1 d-inline-flex align-items-center border border-light-subtle rounded px-2">
                                  <span className="pos-currency-symbol text-muted small me-1">{currencySymbol}</span>
                                  <input
                                    type="number"
                                    min="0"
                                    className="pos-split-input border-0 bg-transparent fw-bold text-dark w-100"
                                    value={item.amount}
                                    placeholder="0"
                                    onChange={(e) => {
                                      const val = e.target.value;
                                      setSplitPayments(splitPayments.map((p, i) => i === idx ? { ...p, amount: val } : p));
                                    }}
                                  />
                                </div>
                              </div>
                            ))}
                          </div>

                          <div className="pos-checkout-btn-wrap mt-3">
                            <button
                              type="submit"
                              className="pos-complete-payment-btn btn btn-primary w-100 d-flex align-items-center justify-content-between fw-bold py-2 px-3 rounded"
                              disabled={isPlaceOrderDisabled}
                              title={placeOrderButtonTitle}
                              onClick={() => {
                                orderFormik.setFieldValue('payment_mode', 'split');
                                if (cart?.discount) orderFormik.setFieldValue('discount', cart.discount);
                              }}
                            >
                              <span></span>
                              <span className="pos-pay-btn-label">Place Order</span>
                              <span className="pos-pay-btn-hotkey">
                                <FontAwesomeIcon icon={faPrint} className="me-1" /> P1
                              </span>
                            </button>
                          </div>
                        </div>
                      ) : null
                      */}
                    </form>
                    <CartDiscountModal amount={orderFormik.values.amount} />
                  </div>
                </div>
              ) : (
                <div className="alert alert-light border text-center py-5 m-3 rounded flex-grow-1 d-flex flex-column align-items-center justify-content-center">
                  <FontAwesomeIcon icon={faCartShopping} className="fs-3 text-muted mb-2 d-block" />
                  <p className="mb-0 fw-semibold text-secondary">Your cart is empty</p>
                  <p className="mb-0 small text-muted">Add products from the catalog to proceed.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {location.pathname === `${routes.sales}` && cart?.lineItems?.length > 0 && (
        <div
          className="position-fixed bottom-0 start-0 end-0 bg-white border-top px-3 py-2 shadow-lg d-flex align-items-center justify-content-between d-md-none"
          style={{ zIndex: 1040 }}
        >
          <div className="d-flex align-items-center gap-2">
            <span className="badge bg-primary rounded-pill px-2 py-1">
              {totalItemCount} {totalItemCount === 1 ? 'item' : 'items'}
            </span>
            <span className="fw-bolder text-dark fs-6">{doAmountFormat(finalTotal)}</span>
          </div>
          <button
            type="button"
            className="btn btn-primary fw-bold px-3 py-2 rounded d-flex align-items-center gap-2 shadow-sm"
            data-bs-toggle="offcanvas"
            data-bs-target="#userSearch"
            aria-controls="userSearch"
          >
            <span>Checkout</span>
            <FontAwesomeIcon icon={faArrowRight} className="small" />
          </button>
        </div>
      )}

      <TodaySalesSummary modal={modal} setModal={setModal} modalId="todaySummaryModalMobile" />
      <CustomerModal modalId="customer-modal-sales" />
    </>
  )
}
export default MobileTemplate